import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from 'react'
import { ChevronDown, Menu, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react'
import { BRAND_FAMILY, BRAND_MODULE, environmentLabel } from '../../lib/brand'

export type NavIcon = ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' }>

export interface NavItem {
  readonly id: string
  readonly label: string
  /** Non-functional items are visibly disabled rather than pretending to work. */
  readonly available: boolean
  /** Shown to assistive technology and as a tooltip on disabled items. */
  readonly note?: string
  /** Presentational only. A missing icon renders a dot, never a gap. */
  readonly icon?: NavIcon
}

/**
 * A named set of related destinations.
 *
 * Introduced because a flat list of fifteen entries gave a billing rule set the
 * same weight as the dashboard, and left a reader to work out from the labels
 * alone which screens they use daily and which they configure once.
 */
export interface NavGroup {
  readonly id: string
  readonly label: string
  readonly items: readonly NavItem[]
  readonly icon?: NavIcon
}

export type NavEntry = NavItem | NavGroup

function isNavGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry
}

export interface ShellIdentity {
  readonly product: string
  readonly tagline: string
  readonly organisation: string
  readonly environment: 'local' | 'sandbox' | 'production'
  readonly userName: string
  readonly userRole: string
  /** Optional, and never the button's label: an address can be sixty characters. */
  readonly userEmail?: string
}

/** Remembering the collapse is a browser preference, so it lives in the browser. */
const COLLAPSE_KEY = 'swamito.nav.collapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    // Private browsing, or storage disabled. A preference is not worth an
    // exception, so the default stands.
    return false
  }
}

/**
 * The application shell: a navy navigation column, a white top bar, and the page
 * canvas.
 *
 * **The navigation has three states, not two**, and the third is why this was
 * rewritten. Expanded and collapsed are the desktop pair. On a phone it is an
 * off-canvas drawer over a backdrop, because the previous behaviour, a wrapping
 * strip of every destination stacked above the page, pushed the actual content
 * below the fold before a reader had seen anything.
 *
 * Nothing here decides what a person may see. The entries arrive already
 * filtered by permission, and collapsing, opening or closing changes only what
 * is drawn.
 */
export default function AppShell({
  identity,
  navItems,
  activeNavId,
  onNavigate,
  onSignOut,
  sectionTabs,
  filterBar,
  children,
}: {
  identity: ShellIdentity
  navItems: readonly NavEntry[]
  activeNavId: string
  onNavigate: (id: string) => void
  /** Ends the session. Lives in the account menu, where people look for it. */
  onSignOut?: () => void
  /**
   * Secondary navigation for the active area, above the page and below the top
   * bar. Rendered here rather than by each page so every screen in a section
   * carries the same strip in the same place.
   */
  sectionTabs?: ReactNode
  filterBar?: ReactNode
  children: ReactNode
}) {
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current
      try {
        window.localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0')
      } catch {
        // See readCollapsed. The session still works, it just forgets.
      }
      return next
    })
  }, [])

  // Escape closes the drawer, and the body does not scroll behind it.
  useEffect(() => {
    if (!drawerOpen) return undefined
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previous
    }
  }, [drawerOpen])

  const navigate = useCallback(
    (id: string) => {
      setDrawerOpen(false)
      onNavigate(id)
    },
    [onNavigate],
  )

  /*
   * The document owns the one vertical scroll. Nothing else does.
   *
   * This used to switch architecture at the `lg` breakpoint: below it the page
   * scrolled normally, above it the shell became `h-screen overflow-hidden` and
   * `<main>` scrolled inside itself. Two models in one application, and the
   * desktop one put its scrollbar in the middle of the window rather than at
   * the edge, beside the gutter the browser draws anyway. It reads as two
   * scrollbars because it very nearly is: one real, one a permanent track.
   *
   * The app-owns-scroll model also costs things people expect to work. Browser
   * scroll restoration, find-in-page scrolling the wrong box, anchor links, and
   * the mobile address bar collapsing all assume the document scrolls.
   *
   * So: `min-h-screen` and no fixed height, no `overflow-hidden`, and no
   * `overflow-y-auto` on main. The sidebar stays full height by being sticky
   * rather than by the shell being clipped, which is also what stops it ending
   * early and leaving a blank strip when the content is taller than the window.
   *
   * Horizontal scroll containers inside tables and charts are untouched: they
   * are a different axis and a different decision.
   */
  return (
    <div className="flex min-h-screen flex-col bg-canvas lg:flex-row">
      {/* ------------------------------------------------- desktop column -- */}
      {/* Sticky, not clipped. It stays the full height of the window while
          the document scrolls past it, so it can never end early. */}
      <div className="hidden lg:sticky lg:top-0 lg:block lg:h-screen lg:shrink-0">
        <SideNav
          items={navItems}
          activeId={activeNavId}
          onNavigate={navigate}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      </div>

      {/* ------------------------------------------------- mobile drawer -- */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="Close navigation"
            data-testid="nav-backdrop"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-slate-900/50"
          />
          <div className="absolute inset-y-0 left-0 w-[17rem] max-w-[85vw] shadow-2xl">
            <SideNav
              items={navItems}
              activeId={activeNavId}
              onNavigate={navigate}
              collapsed={false}
              onClose={() => setDrawerOpen(false)}
            />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          identity={identity}
          onOpenNav={() => setDrawerOpen(true)}
          {...(onSignOut === undefined ? {} : { onSignOut })}
        />

        <main
          id="main-content"
          className="min-w-0 flex-1 px-4 pb-16 pt-5 sm:px-6 lg:px-8 lg:pt-6"
        >
          <div className="mx-auto w-full max-w-[1440px] min-w-0">
            {sectionTabs}
            {filterBar}
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

/* ----------------------------------------------------------------- top -- */

function TopBar({
  identity,
  onOpenNav,
  onSignOut,
}: {
  identity: ShellIdentity
  onOpenNav: () => void
  onSignOut?: () => void
}) {
  return (
    <header className="sticky top-0 z-30 border-b border-line bg-surface px-4 py-2.5 sm:px-6 lg:px-8">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-50 focus:rounded-lg focus:bg-brand-600 focus:px-3 focus:py-1.5 focus:text-[0.8125rem] focus:font-semibold focus:text-white"
      >
        Skip to main content
      </a>

      {/* No wrapping. The row is one line at every width, and the pieces that
          cannot fit truncate rather than pushing the account control onto a
          second row. */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onOpenNav}
          data-testid="nav-open"
          aria-label="Open navigation"
          className="-ml-1 shrink-0 rounded-lg p-2 text-ink-secondary hover:bg-surface-subtle lg:hidden"
        >
          <Menu aria-hidden="true" className="h-5 w-5" />
        </button>

        {/* The wordmark lives in the sidebar, which is off-canvas on a phone,
            so the bar carries the customer: the thing an operator has to be
            certain of before reading a figure. */}
        <p
          className="min-w-0 truncate text-[0.9375rem] font-semibold text-ink"
          data-testid="shell-organisation"
          title={identity.organisation}
        >
          {identity.organisation}
        </p>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          <EnvironmentChip environment={identity.environment} />
          <UserMenu identity={identity} {...(onSignOut === undefined ? {} : { onSignOut })} />
        </div>
      </div>
    </header>
  )
}

/**
 * The product wordmark.
 *
 * Text rather than an image, because no logo asset exists yet and inventing a
 * permanent mark in code would be pretending. When a real logo arrives it
 * replaces the contents of this one component.
 */
function Wordmark() {
  return (
    <p className="flex min-w-0 flex-col gap-0.5" data-testid="shell-wordmark">
      <span className="wordmark truncate text-[1.1875rem] font-bold text-white">
        {BRAND_FAMILY}
      </span>
      <span className="eyebrow truncate text-[0.6875rem] text-brand-300">{BRAND_MODULE}</span>
    </p>
  )
}

/** The collapsed mark: the family initial, so the rail is not a blank strip. */
function WordmarkCompact() {
  return (
    <p
      data-testid="shell-wordmark"
      className="wordmark mx-auto flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-[1rem] font-bold text-white"
    >
      {BRAND_FAMILY.slice(0, 1)}
      <span className="sr-only">
        {BRAND_FAMILY} {BRAND_MODULE}
      </span>
    </p>
  )
}

/**
 * Which environment this is, when it is not the real one.
 *
 * Production shows nothing: a finished product should not tell a customer which
 * server they are on. Anything else says so plainly, because mistaking a
 * sandbox for production is the expensive mistake.
 */
function EnvironmentChip({ environment }: { environment: ShellIdentity['environment'] }) {
  const label = environmentLabel(environment)
  if (label === null) return null
  return (
    <span
      data-testid="shell-environment"
      className="hidden rounded-full border border-brand-200 bg-brand-50 px-2.5 py-0.5 text-[0.75rem] font-medium text-accent-strong sm:inline"
    >
      {label}
    </span>
  )
}

/** Up to two letters from whatever the account actually has. */
function initialsOf(name: string, email?: string): string {
  const source = name.trim() === '' ? (email ?? '') : name
  const words = source.replace(/@.*$/, '').split(/[\s._-]+/).filter(Boolean)
  const letters = words.slice(0, 2).map((word) => word[0] ?? '')
  return letters.join('').toUpperCase() || '?'
}

function Avatar({ initials, large = false }: { initials: string; large?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={[
        'flex shrink-0 items-center justify-center rounded-full bg-brand-100 font-semibold text-accent-strong',
        large ? 'h-10 w-10 text-[0.875rem]' : 'h-7 w-7 text-[0.75rem]',
      ].join(' ')}
    >
      {initials}
    </span>
  )
}

/**
 * The account control and its menu.
 *
 * **A long email address must not be able to set the width of anything.** The
 * previous version put `fullName ?? email` straight into the button with no
 * constraint, so an account with no name recorded stretched the control across
 * the bar and then clipped inside a 224px popover. The button now shows a name
 * only from the small breakpoint up, truncated, and the address appears once, in
 * the panel, where it is allowed to wrap.
 *
 * The panel is capped at the viewport rather than at a fixed width, so at 390 it
 * fits with a margin instead of running off the right edge.
 */
function UserMenu({
  identity,
  onSignOut,
}: {
  identity: ShellIdentity
  onSignOut?: () => void
}) {
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const initials = initialsOf(identity.userName, identity.userEmail)

  // The button shows a name, never an address. When the account has only an
  // address, the button is the avatar and the chevron.
  const hasName =
    identity.userEmail === undefined || identity.userName !== identity.userEmail

  useEffect(() => {
    if (!open) return undefined
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    function onPointerDown(event: MouseEvent) {
      if (
        containerRef.current !== null &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={`Account menu for ${identity.userName}`}
        data-testid="account-menu"
        onClick={() => setOpen((previous) => !previous)}
        className="flex max-w-[13rem] items-center gap-2 rounded-lg border border-line px-1.5 py-1 text-[0.8125rem] font-medium text-ink-secondary transition-colors hover:bg-surface-subtle sm:px-2"
      >
        <Avatar initials={initials} />
        {hasName && (
          <span className="hidden min-w-0 truncate sm:block">{identity.userName}</span>
        )}
        <ChevronDown aria-hidden="true" className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
      </button>

      {open && (
        <div
          id={menuId}
          data-testid="account-panel"
          className="absolute right-0 z-50 mt-2 w-[20rem] max-w-[calc(100vw-1.5rem)] overflow-hidden rounded-xl border border-line bg-surface text-ink shadow-lg"
        >
          <div className="flex items-start gap-3 px-4 py-3.5">
            <Avatar initials={initials} large />
            <div className="min-w-0 flex-1">
              <p className="truncate text-[0.875rem] font-semibold text-ink">
                {identity.userName}
              </p>
              {identity.userEmail !== undefined &&
                identity.userEmail !== identity.userName && (
                  <p
                    data-testid="account-email"
                    // An address has no spaces to break at, so it needs explicit
                    // permission to wrap. Applied here and not to body text.
                    className="mt-0.5 text-[0.8125rem] leading-snug text-ink-secondary [overflow-wrap:anywhere]"
                  >
                    {identity.userEmail}
                  </p>
                )}
              <p className="mt-1 text-[0.75rem] text-ink-muted">{identity.userRole}</p>
            </div>
          </div>

          {onSignOut !== undefined && (
            <div className="border-t border-line px-3 py-2.5">
              <button
                type="button"
                data-testid="sign-out"
                onClick={onSignOut}
                className="rounded-lg border border-line px-3 py-1.5 text-[0.8125rem] font-medium text-ink-secondary transition-colors hover:bg-surface-subtle"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/* ----------------------------------------------------------------- nav -- */

const LEAF_BASE =
  'relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[0.875rem] font-medium transition-colors duration-150'

/** A dot where an icon would be, so an entry without one still aligns. */
function LeafGlyph({ icon: Icon }: { icon?: NavIcon }) {
  if (Icon === undefined) {
    return (
      <span aria-hidden="true" className="flex h-4 w-4 shrink-0 items-center justify-center">
        <span className="h-1.5 w-1.5 rounded-full bg-current opacity-50" />
      </span>
    )
  }
  return <Icon aria-hidden="true" className="h-4 w-4 shrink-0" />
}

function NavLeaf({
  item,
  active,
  nested,
  collapsed,
  onNavigate,
}: {
  item: NavItem
  active: boolean
  nested: boolean
  collapsed: boolean
  onNavigate: (id: string) => void
}) {
  return (
    <button
      type="button"
      disabled={!item.available}
      aria-current={active ? 'page' : undefined}
      // The label is the tooltip when it is the only thing hidden, so a
      // collapsed rail is readable without expanding it.
      title={item.note ?? (collapsed ? item.label : undefined)}
      aria-label={collapsed ? item.label : undefined}
      onClick={() => onNavigate(item.id)}
      className={[
        LEAF_BASE,
        collapsed ? 'justify-center px-0' : '',
        nested && !collapsed ? 'pl-4 text-[0.84375rem]' : '',
        /*
         * Active is a filled panel plus a saffron bar on the leading edge, not
         * a colour change alone. On a dark column a tint by itself is easy to
         * miss and impossible for anyone who cannot separate the two hues.
         */
        active
          ? 'bg-white/[0.10] font-semibold text-white before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-brand-500'
          : 'text-slate-300 hover:bg-white/[0.06] hover:text-white',
        item.available ? '' : 'cursor-not-allowed opacity-40',
      ].join(' ')}
    >
      <LeafGlyph {...(item.icon === undefined ? {} : { icon: item.icon })} />
      {!collapsed && <span className="min-w-0 truncate">{item.label}</span>}
      {!item.available && <span className="sr-only">, not built yet</span>}
    </button>
  )
}

/**
 * A collapsible group.
 *
 * A real button with `aria-expanded`, not a clickable div, so Enter and Space
 * work without any key handling of our own and a screen reader announces the
 * state rather than the caret.
 *
 * On a collapsed rail there is nowhere to put the children, so the group's icon
 * expands the rail instead of opening a flyout. A flyout on a 72px column is a
 * second navigation model to build, test and explain, and pressing the icon to
 * get the labels back is what people try first anyway.
 */
function NavGroupBlock({
  group,
  activeId,
  collapsed,
  onNavigate,
  onExpandRail,
}: {
  group: NavGroup
  activeId: string
  collapsed: boolean
  onNavigate: (id: string) => void
  onExpandRail?: () => void
}) {
  const panelId = useId()
  const holdsActive = group.items.some((item) => item.id === activeId)
  const [open, setOpen] = useState(holdsActive)

  // Follows the route. Navigating into a group from anywhere else, including
  // the browser's back button, opens it.
  useEffect(() => {
    if (holdsActive) setOpen(true)
  }, [holdsActive])

  if (collapsed) {
    return (
      <li>
        <button
          type="button"
          data-testid={`nav-group-${group.id}`}
          data-holds-active={holdsActive ? 'true' : undefined}
          title={group.label}
          aria-label={`${group.label}, expand the navigation to choose`}
          onClick={onExpandRail}
          className={[
            LEAF_BASE,
            'justify-center px-0',
            holdsActive
              ? 'bg-white/[0.10] text-white before:absolute before:inset-y-1.5 before:left-0 before:w-[3px] before:rounded-full before:bg-brand-500'
              : 'text-slate-300 hover:bg-white/[0.06] hover:text-white',
          ].join(' ')}
        >
          <LeafGlyph {...(group.icon === undefined ? {} : { icon: group.icon })} />
        </button>
      </li>
    )
  }

  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        data-testid={`nav-group-${group.id}`}
        data-holds-active={holdsActive ? 'true' : undefined}
        onClick={() => setOpen((current) => !current)}
        className={[
          LEAF_BASE,
          holdsActive ? 'text-white' : 'text-slate-300 hover:bg-white/[0.06] hover:text-white',
        ].join(' ')}
      >
        <LeafGlyph {...(group.icon === undefined ? {} : { icon: group.icon })} />
        <span className="min-w-0 flex-1 truncate">{group.label}</span>
        <ChevronDown
          aria-hidden="true"
          className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-150 ${open ? 'rotate-0' : '-rotate-90'}`}
        />
      </button>

      {open && (
        <ul id={panelId} className="mt-0.5 flex flex-col gap-0.5 border-l border-white/10 pl-2">
          {group.items.map((item) => (
            <li key={item.id}>
              <NavLeaf
                item={item}
                active={item.id === activeId}
                nested
                collapsed={false}
                onNavigate={onNavigate}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

function SideNav({
  items,
  activeId,
  onNavigate,
  collapsed,
  onToggleCollapsed,
  onClose,
}: {
  items: readonly NavEntry[]
  activeId: string
  onNavigate: (id: string) => void
  collapsed: boolean
  onToggleCollapsed?: () => void
  /** Present only in the mobile drawer. */
  onClose?: () => void
}) {
  const leaves = items.filter((entry): entry is NavItem => !isNavGroup(entry))

  return (
    <nav
      aria-label="Sections"
      data-collapsed={collapsed ? 'true' : 'false'}
      /*
       * The navy column. A single warm bloom at the top keeps it from reading
       * as a flat black slab, and it is far short of a gradient that announces
       * itself.
       */
      className={[
        'flex h-full flex-col overflow-y-auto py-4 transition-[width] duration-200',
        collapsed ? 'w-[4.5rem] px-2' : 'w-60 px-3',
      ].join(' ')}
      style={{ background: 'var(--sidebar-surface)' }}
    >
      <div
        className={[
          'mb-4 flex items-center gap-2',
          collapsed ? 'justify-center px-0' : 'px-2',
        ].join(' ')}
      >
        {collapsed ? <WordmarkCompact /> : <Wordmark />}
        {onClose !== undefined && (
          <button
            type="button"
            onClick={onClose}
            data-testid="nav-close"
            aria-label="Close navigation"
            className="ml-auto shrink-0 rounded-lg p-1.5 text-slate-300 hover:bg-white/10 hover:text-white"
          >
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        )}
      </div>

      <ul className="flex flex-1 flex-col gap-0.5">
        {items.map((entry) =>
          isNavGroup(entry) ? (
            <NavGroupBlock
              key={entry.id}
              group={entry}
              activeId={activeId}
              collapsed={collapsed}
              onNavigate={onNavigate}
              {...(onToggleCollapsed === undefined ? {} : { onExpandRail: onToggleCollapsed })}
            />
          ) : (
            <li key={entry.id}>
              <NavLeaf
                item={entry}
                active={entry.id === activeId}
                nested={false}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            </li>
          ),
        )}
      </ul>

      {/* Only shown when something actually is unavailable. In the live
          application every section is built, so this says nothing rather than
          telling a customer about milestones they have no reason to know. */}
      {!collapsed && leaves.some((item) => !item.available) && (
        <p className="mt-3 border-t border-white/10 pt-3 text-[0.75rem] leading-snug text-slate-400">
          Sections shown in grey are not available yet.
        </p>
      )}

      {onToggleCollapsed !== undefined && (
        <div className="mt-3 border-t border-white/10 pt-3">
          <button
            type="button"
            onClick={onToggleCollapsed}
            data-testid="nav-collapse"
            aria-label={collapsed ? 'Expand the navigation' : 'Collapse the navigation'}
            title={collapsed ? 'Expand the navigation' : 'Collapse the navigation'}
            className={[
              LEAF_BASE,
              'text-slate-400 hover:bg-white/[0.06] hover:text-white',
              collapsed ? 'justify-center px-0' : '',
            ].join(' ')}
          >
            {collapsed ? (
              <PanelLeftOpen aria-hidden="true" className="h-4 w-4 shrink-0" />
            ) : (
              <PanelLeftClose aria-hidden="true" className="h-4 w-4 shrink-0" />
            )}
            {!collapsed && <span className="truncate">Collapse</span>}
          </button>
        </div>
      )}
    </nav>
  )
}
